import { add } from '@/core/data/ecommerce';
import { active } from '@/core/data/uikits';
import { defineStore } from 'pinia';

export interface Appearance {
    image: string;
    buttonColor: string;
    fontSizes: string;
}

export interface Condiment {
    id: string;
    name: string;
    price: number;
    preset: boolean;
    appearance: Appearance;
    altDisplayLang: String[];
}

export interface CondimentGroup {
    id: string;
    name: string;
    selectionType: string;
    newLine: boolean;
    condiments: Condiment[];
}

export const defaultCondiment = {
    id: '',
    name: '',
    price: 0,
    preset: false,
    appearance: {
        image: '',
        buttonColor: '',
        fontSizes: ''
    },
    altDisplayLang: []
} as Condiment;

export const defaultCondimentGroup = {
    id: '',
    name: '',
    selectionType: '',
    newLine: false,
    condiments: []
} as CondimentGroup;

export const useCondimentStore = defineStore('condiments', {
    state: () => ({
        condimentGroupsList: [] as CondimentGroup[],
        activeCondimentGroup: { ...defaultCondimentGroup },
        tempActiveCondimentGroup: { ...defaultCondimentGroup },
        activeCondiment: { ...defaultCondiment },
        tempActiveCondiment: { ...defaultCondiment },
    }),
    getters: {
        getActiveCondimentGroup: (state) => {
            return state.activeCondimentGroup;
        },
        getActiveCondimentGroupId: (state) => {
            return state.activeCondimentGroup.id;
        },
        findCondimentGroupById: (state) => (id: string) => {
            return state.condimentGroupsList.find(condimentGroup => condimentGroup.id === id);
        },
        getCondimentGroupsList: (state) => {
            return state.condimentGroupsList;
        },
        getTempActiveCondimentGroup: (state) => {
            return state.tempActiveCondimentGroup;
        },
        getIsActiveCondimentGroupChanged: (state) => {
            return JSON.stringify(state.activeCondimentGroup) !== JSON.stringify(state.tempActiveCondimentGroup);
        },
        getActiveCondiment: (state) => {
            return state.activeCondiment;
        },
        getTempActiveCondiment: (state) => {
            return state.tempActiveCondiment;
        },
        findCondimentById: (state) => (id: string) => {
            return state.activeCondimentGroup.condiments.find(condiment => condiment.id === id);
        },
        getActiveCondimentId: (state) => {
            return state.activeCondiment.id;
        }
    },
    actions: {
        initializeCondimentGroups(condimentGroups: CondimentGroup[]) {
            this.condimentGroupsList = condimentGroups;
        },
        addCondimentGroup(condimentGroup: CondimentGroup) {
            this.condimentGroupsList.push(condimentGroup);
        },
        removeCondimentGroup(id: string) {
            this.condimentGroupsList = this.condimentGroupsList.filter(condimentGroup => condimentGroup.id !== id);
            this.activeCondimentGroup = { ...defaultCondimentGroup };
            this.tempActiveCondimentGroup = { ...defaultCondimentGroup };
        },
        updateCondimentGroup(updatedCondimentGroup: CondimentGroup) {
            const index = this.condimentGroupsList.findIndex(condimentGroup => condimentGroup.id === updatedCondimentGroup.id);
            if (index !== -1) {
                this.condimentGroupsList[index] = updatedCondimentGroup;
            }
        },
        setActiveCondimentGroup(condimentGroup: CondimentGroup) {
            this.activeCondimentGroup = condimentGroup;
        },
        setTempActiveCondimentGroup(condimentGroup: CondimentGroup) {
            this.tempActiveCondimentGroup = condimentGroup;
        },
        initActiveCondimentGroup(condimentGroup: CondimentGroup) {
            this.activeCondimentGroup = condimentGroup;
            this.tempActiveCondimentGroup = condimentGroup;
        },
        setActiveCondiment(condiment: Condiment) {
            this.activeCondiment = condiment;
        },
        setTempActiveCondiment(condiment: Condiment) {
            this.tempActiveCondiment = condiment;
        },
        addCondiment(condiment: Condiment) {
            this.activeCondimentGroup.condiments.push(condiment);
        },
        initActiveCondiment(condiment: Condiment) {
            this.activeCondiment = condiment;
            this.tempActiveCondiment = condiment;
        },
        updateCondiment(updatedCondiment: Condiment) {
            const index = this.activeCondimentGroup.condiments.findIndex(condiment => condiment.id === updatedCondiment.id);
            if (index !== -1) {
                this.activeCondimentGroup.condiments[index] = updatedCondiment;
            }
        },
        removeCondiment(id: string) {
            this.activeCondimentGroup.condiments = this.activeCondimentGroup.condiments.filter(condiment => condiment.id !== id);
            this.activeCondiment = { ...defaultCondiment };
            this.tempActiveCondiment = { ...defaultCondiment };
        },
    },
});